import { Formik } from "formik";
import { useEffect, useState } from "react";
import Spin from "../../../components/Spin";
import { Label } from "../../../components/ui/label";
import CustomLabelText from "../../../components/CustomLabelText";
import { Input } from "../../../components/ui/input";
import { API, fileUpload, get } from "../../../config";
import SearchSelect from "../../../components/SearchSelect";
import { SuburbList, useStateList } from "../../../lib/OptionsAPI";
import { Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import PostalCodeInput from "../../../components/PostalCodeInput";
import { useOrganization } from "../../../lib/context/OrganisationContext";
import { MultiSelect } from "../../../components/SearchMultiSelect";
import { AddOfficeSchema } from "../../../lib/Validation";
import SuburbSelect from "../../../components/SuburbSelect";

const AddEditOffice = ({
  id,
  toggleShowAddEditOffice,
  edit,
  fetchOfficeData,
  orgId,
}) => {
  const [officeData, setOfficeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suburbLoading, setSuburbLoading] = useState(false);
  const [suburbListing, setSuburbListing] = useState([]);
  const [suburbList, setSuburbList] = useState([]);

  const { organizationData } = useOrganization();

  const FetchSuburbList = async (state_id) => {
    await SuburbList(state_id, 1, setSuburbLoading, setSuburbList, true);
  };

  useEffect(() => {
    if (suburbList) {
      let suburbTempListing = suburbList?.map((item) => ({
        ...item,
        value: item.id,
        label: item.name,
      }));
      setSuburbListing(suburbTempListing || []);
    } else {
      setSuburbListing([]);
    }
  }, [suburbList]);

  async function fetchData() {
    try {
      setLoading(true);
      const reponse = await get(`${API.OFFICE}/` + id);
      setOfficeData(reponse?.data?.data);
      let data = reponse?.data?.data;
      if (data?.state_id ? data?.state_id : false) {
        FetchSuburbList(data?.state_id ? data?.state_id : "", "");
        if (data?.suburb_id ? data?.suburb_id : false) {
          setSuburbList([
            data?.suburb
              ? {
                  ...data?.suburb,
                  value: data?.suburb.id,
                  label: data?.suburb.name,
                }
              : {},
          ]);
        }
      }
      setLoading(false);
    } catch (e) {
      setOfficeData(null);
    }
  }
  useEffect(() => {
    if (edit) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // const orgID = organizationData?.organizationList?.find(
  //   (item) => item?.value === officeData?.organization_id
  // )?.value;

  // State
  const { stateLoading, stateList = [] } = useStateList();
  const stateListing = stateList?.map((item) => {
    const { id: value, name, short_name, ...rest } = item;
    return { label: name + ` (${short_name})`, value, ...rest };
  });
  const state = stateListing?.find(
    (item) => item?.value === (officeData?.state_id ? officeData?.state_id : "")
  )?.value;

  const handleFormSubmit = async (value) => {
    const formData = new FormData();

    formData.append("street", value?.street ? value?.street : "");
    formData.append("state_id", value?.state_id ? value?.state_id : "");
    formData.append("suburb_id", value?.suburb_id ? value?.suburb_id : "");
    formData.append(
      "postal_code",
      value?.postal_code ? value?.postal_code : ""
    );

    for (let i = 0; i < value?.organization_id?.length; i++) {
      formData.append(`organization_ids[${i}]`, value?.organization_id[i]);
    }

    if (edit) {
      formData.append("_method", "put");
    }
    try {
      setLoading(true);
      const { data } = await fileUpload(
        `${API.OFFICE}${edit ? `/${id}` : ""}`,
        formData
      );
      setLoading(false);
      if (data) {
        toggleShowAddEditOffice();
        fetchOfficeData();
      }
      return data?.data;
    } catch (error) {
      setLoading(false);
    }
  };
  return (
    <Formik
      initialValues={{
        organization_id: officeData?.organization_ids
          ? officeData?.organization_ids
          : [orgId],
        street: officeData?.street,
        state_id: state,
        suburb_id: officeData?.suburb_id ? officeData?.suburb_id : "",
        postal_code: officeData?.postal_code
          ? officeData?.postal_code.toString()
          : "",
      }}
      enableReinitialize={true}
      onSubmit={handleFormSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={AddOfficeSchema}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isValid,
      }) => (
        <>
          {loading && <Spin expand={true} fullscreen={true} />}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="entry-section md:m-0 m-5"
          >
            <div className="form-box overflow-y-auto overflow-x-hidden max-h-[80vh] p-4 border rounded-lg">
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="street">
                      <CustomLabelText
                        required={true}
                        labelText="Street Address"
                        errorField={errors?.street}
                      />
                    </Label>
                    <Input
                      id="street"
                      placeholder="Enter Street"
                      required
                      name="street"
                      onChange={(e) => {
                        setFieldValue("street", e?.target?.value);
                      }}
                      onBlur={handleBlur}
                      value={values?.street}
                      className={
                        errors?.street &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="state_id">
                      <CustomLabelText
                        required={true}
                        labelText="State"
                        errorField={errors?.state_id}
                      />
                    </Label>
                    <div className="relative">
                      <SearchSelect
                        frameworks={stateListing}
                        optionType={"State"}
                        clearable={true}
                        onClear={() => {
                          setFieldValue("state_id", "");
                          setFieldValue("suburb_id", "");
                          setSuburbList([]);
                        }}
                        handleSelect={(e) => {
                          setFieldValue("state_id", e);
                          setFieldValue("suburb_id", "");
                          setSuburbList([]);
                          FetchSuburbList(e);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.state_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        disabled={false}
                        initialValue={values?.state_id}
                      />
                      {stateLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <div className="min-w-0">
                      <Label htmlFor="suburb_id">
                        <CustomLabelText
                          required={true}
                          labelText="Suburb"
                          errorField={errors?.suburb_id}
                        />
                      </Label>
                      <div className="relative mt-2">
                        <SuburbSelect
                          state_id={values?.state_id}
                          frameworkData={suburbListing}
                          loading={suburbLoading}
                          optionType={"Suburb"}
                          clearable={true}
                          onClear={() => {
                            setFieldValue("suburb_id", "");
                          }}
                          handleSelect={(e) => {
                            setFieldValue("suburb_id", e);
                          }}
                          handleBlur={handleBlur}
                          className={
                            errors?.suburb_id &&
                            `focus-visible:ring-red-500 border-red-500`
                          }
                          disabled={values?.state_id ? false : true}
                          initialValue={values?.suburb_id}
                          setSuburbList={setSuburbList} // Update suburb list as new data loads
                          setSuburbLoading={setSuburbLoading}
                        />
                        {suburbLoading && (
                          <span className="absolute top-2 right-2 animate-spin">
                            <Loader2 width={20} height={20} />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="postal_code">
                      <CustomLabelText
                        required={true}
                        labelText="Postal Code"
                        errorField={errors?.postal_code}
                      />
                    </Label>
                    <PostalCodeInput
                      id="postal_code"
                      placeholder="Enter Postal Code"
                      required={true}
                      name="postal_code"
                      handleChange={(e) => {
                        setFieldValue("postal_code", e);
                      }}
                      handleBlur={handleBlur}
                      className="justify-start"
                      inputClassName={
                        errors?.postal_code &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                      value={values?.postal_code ? values?.postal_code : ""}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="organization_id">
                      <CustomLabelText
                        required={false}
                        labelText="Organization"
                        errorField={errors?.organization_id}
                      />
                    </Label>
                    <div className="relative">
                      <MultiSelect
                        options={
                          organizationData?.organizationList?.length > 0
                            ? organizationData?.organizationList
                            : []
                        }
                        placeholder={"Organization"}
                        onValueChange={(e) => {
                          setFieldValue("organization_id", e);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.organization_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        variant="inverted"
                        animation={2}
                        maxCount={2}
                        defaultValue={values?.organization_id}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit" disabled={!isValid}>
                Save
              </Button>
              <Button
                className="reset ml-3"
                data-dismiss="modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  toggleShowAddEditOffice();
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default AddEditOffice;
